import * as React from 'react';
import { Search, X } from 'lucide-react';
import { Input } from '../../core/input';
import { cn } from '../../../utils';

type SearchInputProps = {
  /** Initial text; the component keeps its own state and debounces `onChange`. */
  defaultValue?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Milliseconds to wait after the last keystroke. 0 = immediate. */
  debounceMs?: number;
  clearLabel?: string;
  className?: string;
  'aria-label'?: string;
};

export function SearchInput({
  defaultValue = '',
  onChange,
  placeholder,
  debounceMs = 200,
  clearLabel = 'Clear search',
  className,
  'aria-label': ariaLabel,
}: SearchInputProps) {
  const [text, setText] = React.useState(defaultValue);
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;
  const first = React.useRef(true);

  React.useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    if (debounceMs <= 0) {
      onChangeRef.current(text);
      return;
    }
    const id = window.setTimeout(() => onChangeRef.current(text), debounceMs);
    return () => window.clearTimeout(id);
  }, [text, debounceMs]);

  return (
    <div className={cn('relative w-full sm:w-72', className)}>
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        type="search"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel ?? placeholder}
        className="pl-9 pr-8 [&::-webkit-search-cancel-button]:hidden"
      />
      {text ? (
        <button
          type="button"
          onClick={() => setText('')}
          aria-label={clearLabel}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </div>
  );
}
